import decryptData from "../../../decrypt";
import { handleLogout } from "../../../config/axios";

const isMobileDevice = () => {
  return (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    (window.matchMedia && window.matchMedia("(max-width: 768px)").matches)
  );
};

const downloadFileWithMobileSupport = (blob: Blob, fileName: string) => {
  const blobUrl = URL.createObjectURL(blob);
  if (isMobileDevice()) {
    const newWindow = window.open(blobUrl, "_blank");
    if (!newWindow) {
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      link.style.display = "none";
      document.body.appendChild(link);
      const clickEvent = new MouseEvent("click", { view: window, bubbles: true, cancelable: true });
      link.dispatchEvent(clickEvent);
      setTimeout(() => { document.body.removeChild(link); URL.revokeObjectURL(blobUrl); }, 100);
    } else {
      setTimeout(() => { URL.revokeObjectURL(blobUrl); }, 1000);
    }
  } else {
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    setTimeout(() => { document.body.removeChild(link); URL.revokeObjectURL(blobUrl); }, 100);
  }
};

interface ExportFilters {
  agenti: string[];
  stato_cliente?: string;
  codice_cliente?: string;
}

export const downloadAnagraficaCSV = async (filters: ExportFilters) => {
  const token = localStorage.getItem("token");
  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };

  // 1. Fetch all customers (same query as the page, no pagination limit)
  const utentiRes = await fetch("/nodeapi/utenti", {
    method: "POST",
    headers,
    body: JSON.stringify({
      agenti: filters.agenti,
      page: 1,
      pageSize: 100000,
      stato_cliente: filters.stato_cliente || "Tutti",
      codice_cliente: filters.codice_cliente || null,
    }),
  });
  if (utentiRes.status === 401 || utentiRes.status === 403 || utentiRes.status === 500) {
    handleLogout();
    return;
  }
  const utentiResult = await utentiRes.json();
  const utentiDecrypted = decryptData(utentiResult.encryptedData, utentiResult.encryptedKey, utentiResult.iv);
  const { data: customers } = JSON.parse(utentiDecrypted);

  if (!customers || customers.length === 0) {
    console.warn("Nessun cliente disponibile per l'esportazione.");
    return;
  }

  // 2. Fetch all customer_detail (pivoted: one record per customer)
  const detailRes = await fetch("/nodeapi/customer-detail/export", { method: "GET", headers });
  const detailResult = await detailRes.json();
  const detailDecrypted = decryptData(detailResult.encryptedData, detailResult.encryptedKey, detailResult.iv);
  const detailRows: Record<string, string>[] = JSON.parse(detailDecrypted);

  // Build lookup map: customer_id -> detail attributes
  const detailMap = new Map<string, Record<string, string>>();
  for (const row of detailRows) {
    const { customer_id, ...attrs } = row;
    detailMap.set(customer_id, attrs);
  }

  // 3. Collect all detail attribute keys (to define extra columns)
  const detailKeys = Array.from(
    detailRows.reduce((acc, row) => {
      Object.keys(row).filter(k => k !== "customer_id").forEach(k => acc.add(k));
      return acc;
    }, new Set<string>())
  );

  // 4. Build merged rows
  const baseKeys = ["CODICE_CLIENTE", "RAGIONE_SOCIALE", "INDIRIZZO_CLIENTE", "LOCALITA_CLIENTE", "CAP", "STATO_CLIENTE", "DESCRIZIONE_AGENTE"];
  const allKeys = [...baseKeys, ...detailKeys];

  const headerRow = allKeys.join(";");
  const csvRows = customers.map((c: Record<string, string>) => {
    const detail = detailMap.get(c.CODICE_CLIENTE) ?? {};
    const row = [
      ...baseKeys.map(k => `"${(c[k] ?? "").replace(/"/g, '""')}"`),
      ...detailKeys.map(k => `"${(detail[k] ?? "").replace(/"/g, '""')}"`),
    ];
    return row.join(";");
  });

  const csvString = [headerRow, ...csvRows].join("\r\n");
  const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
  downloadFileWithMobileSupport(blob, `anagrafica_clienti_${new Date().toISOString().slice(0, 10)}.csv`);
};
